// NodeTypeMutationService.js

import { functions, httpsCallable } from 'firebaseConfig';
import {
  formatDatabaseTypeLabel,
  getDatabaseTypeLabelFormatError,
} from 'utilities/databaseTypeLabels';
import {
  buildTenantCallableFields,
  resolveBaseKey,
} from 'utilities/basePersistence';
import { patchNodeTypeMissionsForBase } from 'services/nodetypes/NodeTypesCacheService';

const callable = (name) => httpsCallable(functions, name);

export async function mutateNodeType(payload = {}) {
  const tenantKey = resolveBaseKey(payload) || String(payload?.baseKey || '').trim();
  const nextPayload = { ...payload };
  delete nextPayload.baseKey;
  delete nextPayload.baseKey;
  const fn = callable('mutateNodeType');
  const { data } = await fn({
    ...nextPayload,
    ...buildTenantCallableFields(tenantKey),
  });
  return data || null;
}

export async function addNodeTypeField({
  nodeKey,
  typeKey,
  baseKey,
  field,
  insertBeforeName,
} = {}) {
  if (!typeKey || !field) {
    throw new Error('addNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.add',
    nodeKey,
    typeKey,
    ...buildTenantCallableFields(baseKey),
    field,
    insertBeforeName: insertBeforeName || null,
  });
}

export async function updateNodeTypeField({
  nodeKey,
  typeKey,
  baseKey,
  field,
  reconcileChecklistPolicy,
} = {}) {
  if (!typeKey || !field) {
    throw new Error('updateNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.update',
    nodeKey,
    typeKey,
    ...buildTenantCallableFields(baseKey),
    field,
    reconcileChecklistPolicy: reconcileChecklistPolicy || null,
  });
}

export async function deleteNodeTypeField({
  nodeKey,
  typeKey,
  baseKey,
  fieldName,
} = {}) {
  if (!typeKey || !fieldName) {
    throw new Error('deleteNodeTypeField: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.delete',
    nodeKey,
    typeKey,
    ...buildTenantCallableFields(baseKey),
    fieldName,
  });
}

export async function reorderNodeTypeFields({
  nodeKey,
  typeKey,
  baseKey,
  orderedFields,
} = {}) {
  if (!typeKey || !Array.isArray(orderedFields)) {
    throw new Error('reorderNodeTypeFields: invalid arguments');
  }

  return mutateNodeType({
    action: 'field.reorder',
    nodeKey,
    typeKey,
    ...buildTenantCallableFields(baseKey),
    orderedFields,
  });
}

export async function updateNodeTypeIconKey({ baseKey, typeKey, iconKey } = {}) {
  if (!baseKey || !typeKey) {
    throw new Error('updateNodeTypeIconKey: invalid arguments');
  }

  return mutateNodeType({
    action: 'icon.update',
    baseKey,
    typeKey,
    iconKey: typeof iconKey === 'string' ? iconKey : '',
  });
}

export async function updateNodeTypeMeta({
  baseKey,
  typeKey,
  label,
  labelPlural,
  description,
  iconKey,
  defaultView,
  showPeekOnOpen = true,
  allowTopLevelCreate,
  showInMenu,
  showInAddMenu,
  appearAs,
} = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeMeta: invalid arguments');
  }

  const normalizedLabel = formatDatabaseTypeLabel(label);
  const normalizedPlural = formatDatabaseTypeLabel(labelPlural) || normalizedLabel;
  const normalizedDescription = String(description || '').trim();
  const normalizedIconKey = String(iconKey || '').trim();
  const normalizedDefaultView = String(defaultView || '').trim().toUpperCase();
  const normalizedShowPeekOnOpen = showPeekOnOpen !== false;
  const hasAllowTopLevelCreate = typeof allowTopLevelCreate === 'boolean';
  const hasShowInMenu = typeof showInMenu === 'boolean';
  const hasShowInAddMenu = typeof showInAddMenu === 'boolean';
  const hasAppearAs = Array.isArray(appearAs);

  if (!normalizedLabel) {
    throw new Error('updateNodeTypeMeta: label is required');
  }

  const labelFormatError = getDatabaseTypeLabelFormatError(normalizedLabel);
  if (labelFormatError) {
    throw new Error(labelFormatError);
  }

  const pluralFormatError = getDatabaseTypeLabelFormatError(normalizedPlural);
  if (pluralFormatError) {
    throw new Error(pluralFormatError);
  }

  const mutationPayload = {
    action: 'type.updateMeta',
    ...buildTenantCallableFields(baseKey),
    typeKey,
    label: normalizedLabel,
    labelPlural: normalizedPlural || normalizedLabel,
    description: normalizedDescription,
    iconKey: normalizedIconKey,
    defaultView: normalizedDefaultView,
    showPeekOnOpen: normalizedShowPeekOnOpen,
  };

  // Only persist when callers explicitly pass the flag so partial meta updates
  // (e.g. label-only) do not stamp true onto child-only system types.
  if (hasAllowTopLevelCreate) {
    mutationPayload.allowTopLevelCreate = allowTopLevelCreate !== false;
  }
  if (hasShowInMenu) {
    mutationPayload.showInMenu = showInMenu !== false;
  }
  if (hasShowInAddMenu) {
    mutationPayload.showInAddMenu = showInAddMenu !== false;
  }
  if (hasAppearAs) {
    mutationPayload.appearAs = appearAs;
  }

  const isScopeNotFoundError = (error) => {
    const code = String(error?.code || '').toLowerCase();
    const message = String(error?.message || '').toLowerCase();
    return code === 'functions/not-found'
      || code === 'not-found'
      || message.includes('node type not found');
  };

  try {
    return await mutateNodeType(mutationPayload);
  } catch (error) {
    // Root/system types live in the global collection. If the UI passed a base
    // key and that scoped doc does not exist, retry against root scope.
    if (!baseKey || !isScopeNotFoundError(error)) {
      throw error;
    }

    return mutateNodeType({
      ...mutationPayload,
      baseKey: null,
    });
  }
}

export async function updateNodeTypeMissions({ baseKey, typeKey, missions } = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeMissions: invalid arguments');
  }

  const result = await mutateNodeType({
    action: 'type.updateMissions',
    ...buildTenantCallableFields(baseKey),
    typeKey,
    missions: missions && typeof missions === 'object' && !Array.isArray(missions)
      ? missions
      : { templates: [] },
  });

  patchNodeTypeMissionsForBase({
    baseKey: baseKey || null,
    missions: result?.missions,
    typeKey,
  });

  return result;
}

export async function updateNodeTypeRollups({ baseKey, typeKey, rollups } = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeRollups: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.updateRollups',
    ...buildTenantCallableFields(baseKey),
    typeKey,
    rollups: Array.isArray(rollups) ? rollups : [],
  });
}

export async function updateNodeTypeRelated({
  baseKey,
  typeKey,
  suggestedChildren,
} = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeRelated: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.updateRelated',
    ...buildTenantCallableFields(baseKey),
    typeKey,
    suggestedChildren: Array.isArray(suggestedChildren) ? suggestedChildren : [],
  });
}

export async function updateNodeTypeViewGroups({
  baseKey,
  typeKey,
  viewGroups,
} = {}) {
  if (!typeKey) {
    throw new Error('updateNodeTypeViewGroups: invalid arguments');
  }

  return mutateNodeType({
    action: 'type.updateViewGroups',
    ...buildTenantCallableFields(baseKey),
    typeKey,
    viewGroups: Array.isArray(viewGroups) ? viewGroups : [],
  });
}

export async function deleteNodeType({
  baseKey,
  typeKey,
  deleteNodes = false,
  module = null,
} = {}) {
  if (!baseKey || !typeKey) {
    throw new Error('deleteNodeType: invalid arguments');
  }

  const cleanedModule = String(module || '').trim().toLowerCase();
  return mutateNodeType({
    action: 'type.delete',
    baseKey,
    typeKey,
    deleteNodes: deleteNodes === true,
    ...(cleanedModule === 'public' || cleanedModule === 'private'
      ? { module: cleanedModule }
      : {}),
  });
}

export async function purgeNodeTypeBaseData({
  baseKey,
  typeKey,
  deleteNodes = false,
  module = null,
} = {}) {
  if (!baseKey || !typeKey) {
    throw new Error('purgeNodeTypeBaseData: invalid arguments');
  }

  const cleanedModule = String(module || '').trim().toLowerCase();
  return mutateNodeType({
    action: 'type.purgeBaseData',
    baseKey,
    typeKey,
    deleteNodes: deleteNodes === true,
    ...(cleanedModule === 'public' || cleanedModule === 'private'
      ? { module: cleanedModule }
      : {}),
  });
}
